<template>
  <div class="container">
    <div class="container__header">
      <DynamicHeading :level="headingLevel" class="container__header-heading">
        {{ headerTitle ?? $t('productCategory.EDIT_TITLES_HEADING') }}
      </DynamicHeading>
      <BaseSegmentedButtons
        :options="selectLocaleOptions"
        @onSetActiveButton="updateSelectedLocaleCode"
      />
    </div>
    <div class="container__inputs">
      <BaseTextInput
        :name="`title-${selectedLocaleCode}`"
        class="container__inputs-input"
        :label="titleLabel"
        :value="titles[selectedLocaleCode]"
        :placeholder="titleLabel"
        :ai="hasCompanyAi"
        :disabled="isDisabled"
        @onAi="openAiTitleModal"
        @change="handleTitleInput"
      />

      <HugeRteTextArea
        v-if="isDescription"
        :name="`description-${selectedLocaleCode}`"
        :label="descriptionLabel"
        :label-description="descriptionHelpText"
        :value="descriptions[selectedLocaleCode]"
        preset="product"
        @input="handleDescriptionInput"
        @onAi="openAiDescriptionModal"
      />
    </div>

    <Ai
      v-if="isAiTitleModalOpen"
      :localized-content="titles"
      :selected-locale="selectedLocale"
      :field-name="$t('TITLE')"
      @onClose="closeAiTitleModal"
      @onSubmitText="handleTitleInputAi"
    />

    <Ai
      v-if="isAiDescriptionModalOpen"
      :localized-content="descriptions"
      :selected-locale="selectedLocale"
      :field-name="$t('DESCRIPTION')"
      @onClose="closeAiDescriptionModal"
      @onSubmitText="handleDescriptionInputAi"
    />
  </div>
</template>

<script>
import Ai from '@/components/generics/ai/Ai.vue'
import BaseTextInput from '@/components/generics/base/BaseTextInput.vue'
import HugeRteTextArea from '@/components/generics/HugeRteTextArea.vue'
import { useCurrentCompany } from '@/hooks/swr/useCurrentCompany'
import { useCompanyLocale } from '@/hooks/useCompanyLocale'
import { ref } from 'vue'
import BaseSegmentedButtons from '../generics/base/BaseSegmentedButtons.vue'
import DynamicHeading from '../layout/DynamicHeading.vue'

export default {
  components: {
    Ai,
    BaseTextInput,
    BaseSegmentedButtons,
    DynamicHeading,
    HugeRteTextArea,
  },
  props: {
    headingLevel: {
      type: Number,
      required: true,
    },

    headerTitle: {
      type: String,
      required: false,
    },

    titleLabel: {
      type: String,
      required: true,
    },

    descriptionLabel: {
      type: String,
      required: false,
      default: '',
    },

    descriptionHelpText: {
      type: String,
      required: false,
      default: '',
    },

    titles: {
      type: Object,
      required: true,
    },

    descriptions: {
      type: Object,
      required: false,
      default: () => {},
    },

    isDescription: {
      type: Boolean,
      required: false,
      default: true,
    },

    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['onUpdatelocalizedTitle', 'onUpdatelocalizedDescription'],
  setup() {
    const { data: currentCompany } = useCurrentCompany()
    const {
      contentLocaleOptions: locales,
      contentLocaleDisplayOptions: selectLocaleOptions,
      companyLocaleCode,
    } = useCompanyLocale()

    const selectedLocaleCode = ref(companyLocaleCode.value)
    return { currentCompany, locales, selectLocaleOptions, selectedLocaleCode }
  },

  data() {
    return {
      isAiTitleModalOpen: false,
      isAiDescriptionModalOpen: false,
    }
  },

  computed: {
    selectedLocale() {
      return this.locales.find(
        (locale) => locale.code === this.selectedLocaleCode
      )
    },

    hasCompanyAi() {
      return this.currentCompany.ai_features_enabled
    },
  },

  methods: {
    updateSelectedLocaleCode(option) {
      this.selectedLocaleCode = option.value
    },

    handleTitleInputAi(value) {
      this.closeAiTitleModal()
      this.handleTitleInput(value)
    },

    handleDescriptionInputAi(value) {
      this.closeAiDescriptionModal()
      this.handleDescriptionInput(value)
    },

    handleTitleInput(value) {
      this.$emit('onUpdatelocalizedTitle', {
        locale: this.selectedLocaleCode,
        value,
      })
    },

    handleDescriptionInput(value) {
      this.$emit('onUpdatelocalizedDescription', {
        locale: this.selectedLocaleCode,
        value,
      })
    },

    openAiTitleModal() {
      this.isAiTitleModalOpen = true
    },

    closeAiTitleModal() {
      this.isAiTitleModalOpen = false
    },

    openAiDescriptionModal() {
      this.isAiDescriptionModalOpen = true
    },

    closeAiDescriptionModal() {
      this.isAiDescriptionModalOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
.container {
  @include cardStyle;
  padding: $spacing-s;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: $spacing-3xs;
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: $spacing-4xs;
  }

  &__inputs {
    display: flex;
    flex-direction: column;
    gap: $spacing-3xs;
    width: 100%;
  }

  &__inputs-input {
    width: 100%;
  }
}
</style>
